import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, OnInit, ViewEncapsulation, computed, effect, inject, untracked } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggle } from '@angular/material/slide-toggle';

import { isEqual } from 'lodash-es';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, take } from 'rxjs/operators';

import { NgSelectComponent, SemerisSelectOption } from '../../../shared/components/ng-select/ng-select.component';
import { SmartDropdownOption } from '../../../shared/components/smart-dropdown/smart-dropdown.component';
import { UserEntityControlFormComponent } from '../../../shared/components/user-entity-control-form/user-entity-control-form.component';
import { CopyToDirective } from '../../../shared/directives/copy/copy-to.directive';
import { PermissionsDirective } from '../../../shared/directives/permissions.directive';
import { Permission } from '../../../shared/models/api/permissions.enum';
import { ShareableEntity } from '../../../shared/models/common/user-entity-base.model';
import { TemplateEntity, UserTemplateEntityBase } from '../../../shared/models/common/user-template-entity-base.model';
import { FormControlErrorPipe } from '../../../shared/pipes/form-control-error.pipe';
import { PermissionService } from '../../../shared/services/permission.service';
import { AIContribution } from '../../../unified-datapoint/constants/ai-contribution.enum';
import {
  AIContributionOptions,
  PresentationTypeOptions,
  VerificationTypeOptions,
} from '../../../unified-datapoint/constants/verification-type.options';
import { DatapointField } from '../../../unified-datapoint/models/datapoint-field.models';
import {
  DatapointRule,
  DatapointRuleType,
  PresentationType,
  RuleBase,
  TtlOptions,
  VerificationType,
} from '../../../unified-datapoint/models/datapoint-rule.model';
import { Topic } from '../../models/topic.model';
import { TemplateModalType } from '../edit-template-modal/template-modal.models';

@Component({
  selector: 'semeris-template-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PermissionsDirective,
    NgSelectComponent,
    MatInputModule,
    MatSlideToggle,
    FormControlErrorPipe,
    UserEntityControlFormComponent,
    CopyToDirective,
  ],
  templateUrl: './template-form.component.html',
  styleUrls: ['./template-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateFormComponent implements OnInit {
  private readonly permissionService = inject(PermissionService);

  public readonly item = input<UserTemplateEntityBase>();
  public readonly type = input<TemplateModalType>();
  public readonly disabled = input<boolean>();
  public readonly hasIntegrationIdentifier = input<boolean>();
  public readonly hasDisplayShortName = input<boolean>();
  public readonly assets = input<SmartDropdownOption[]>();
  public readonly datapointFieldOptions = input<DatapointField[]>();
  public readonly presentationType = input<PresentationType>();
  public readonly verificationType = input<VerificationType>();
  public readonly aiContribution = input<AIContribution>();

  public readonly Permissions = Permission;
  public readonly TemplateModalType = TemplateModalType;

  public readonly VerificationTypeOptions: SemerisSelectOption[] = VerificationTypeOptions;
  public readonly PresentationTypeOptions: SemerisSelectOption[] = PresentationTypeOptions;
  public readonly AIContributionOptions: SemerisSelectOption[] = AIContributionOptions;
  public readonly TtlOptions: SemerisSelectOption[] = TtlOptions;

  public showPresentationType = computed(() => {
    return (this.item() as DatapointRule)?.ruleType === DatapointRuleType.Formula || this.type() !== TemplateModalType.RuleBase;
  });

  public groundTruthAvailable = computed(() => {
    return (
      (this.item() as DatapointRule)?.ruleType === DatapointRuleType.Formula ||
      (this.item() as DatapointRule)?.ruleType === DatapointRuleType.LLM
    );
  });

  public showTtl = computed(() => {
    return (
      this.type() === TemplateModalType.RuleBase &&
      (this.item() as DatapointRule)?.ruleType !== DatapointRuleType.LLM &&
      !!(this.item() as DatapointRule)?.ruleType
    );
  });

  public readonly DatapointRuleType = DatapointRuleType;

  public form = new FormGroup({
    displayName: new FormControl<string>(null, Validators.required),
    displayShortName: new FormControl<string>(null),
    referenceNumber: new FormControl<string>(null),
    description: new FormControl<string>(null),
    accessControl: new FormControl<ShareableEntity>(undefined),
    templateControl: new FormControl<TemplateEntity>(undefined),
    assetShortNames: new FormControl<string[]>([], Validators.required),
    verificationType: new FormControl<VerificationType>(null),
    presentationType: new FormControl<PresentationType>(null),
    integrationIdentifier: new FormControl<string>('', Validators.pattern(/^[A-Za-z0-9-:]+$/)),
    groundTruthDatapointField: new FormControl<string>(null),
    aiContribution: new FormControl<AIContribution>(null),
    details: new FormControl<string>(null),
    statsEnabled: new FormControl<boolean>(false),
    statsNumericTransform: new FormControl<string>(null),
    statsCalculateNumeric: new FormControl<boolean>(false),
    metaValueDescription: new FormControl<string>(null),
    metaExample: new FormControl<string>(null),
    metaValueIssues: new FormControl<string>(null),
    ttlSeconds: new FormControl<number>(null),
    builtIn: new FormControl<boolean>(false),
    topicShortName: new FormControl<string>(null),
  });

  public publishDisabled$: Observable<boolean> = this.form.valueChanges.pipe(
    distinctUntilChanged((a, b) => isEqual(a, b)),
    map((value) => {
      return this.type() === TemplateModalType.RuleBase && value.description?.length < 10;
    }),
  );

  constructor() {
    effect(() => {
      const item = this.item();
      const type = this.type();
      const aiContribution = untracked(this.aiContribution);
      const presentationType = untracked(this.presentationType);
      const verificationType = untracked(this.verificationType);

      if (this.form.disabled) {
        this.form.enable();
      }

      this.form.setValue({
        displayName: item?.displayName || '',
        displayShortName: item?.displayShortName || '',
        description: item?.description || '',
        referenceNumber: item?.referenceNumber || '',
        assetShortNames: item?.assetShortNames || [],
        integrationIdentifier: item?.integrationIdentifier || '',
        accessControl: {
          shared: item?.shared || false,
          sharedForEdit: item?.sharedForEdit || false,
        },
        templateControl: {
          template: item?.template || false,
          hidden: item?.hidden || false,
          templateTenantAccess: item?.templateTenantAccess || [],
        },
        presentationType: type === TemplateModalType.RuleBase ? presentationType || null : null,
        verificationType: type === TemplateModalType.RuleBase ? verificationType || null : null,
        groundTruthDatapointField: this.groundTruthAvailable() ? (item as DatapointRule)?.groundTruthDatapointField || null : null,
        aiContribution: type === TemplateModalType.RuleBase ? aiContribution || null : null,
        details: type === TemplateModalType.RuleBase ? (item as RuleBase)?.details || '' : '',
        statsEnabled: type === TemplateModalType.RuleBase ? (item as RuleBase)?.stats?.enabled || false : false,
        statsNumericTransform: type === TemplateModalType.RuleBase ? (item as RuleBase)?.stats?.numericTransform || null : null,
        statsCalculateNumeric: type === TemplateModalType.RuleBase ? (item as RuleBase)?.stats?.calculateNumeric || false : false,
        metaValueDescription: type === TemplateModalType.RuleBase ? (item as RuleBase)?.meta?.valueDescription || null : null,
        metaExample: type === TemplateModalType.RuleBase ? (item as RuleBase)?.meta?.example || null : null,
        metaValueIssues: type === TemplateModalType.RuleBase ? (item as RuleBase)?.meta?.valueIssues || null : null,
        ttlSeconds: type === TemplateModalType.RuleBase ? (item as RuleBase)?.ttlSeconds || null : null,
        builtIn: !!(item as Topic)?.builtIn,
        topicShortName: (item as Topic)?.shortName || null,
      });

      const disabled = untracked(this.disabled);
      if (disabled) {
        this.form.disable();
      }
    });

    effect(() => {
      const disabled = this.disabled();
      if (disabled) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    });
  }

  public ngOnInit(): void {
    this.permissionService
      .hasEveryPermission([Permission.WRITE_DATAPOINT_META])
      .pipe(take(1))
      .subscribe((hasPermission) => {
        if (!hasPermission) {
          this.form.controls.verificationType.disable({ emitEvent: false });
          this.form.controls.presentationType.disable({ emitEvent: false });
          this.form.controls.aiContribution.disable({ emitEvent: false });
          this.form.controls.metaValueDescription.disable({ emitEvent: false });
          this.form.controls.metaExample.disable({ emitEvent: false });
          this.form.controls.metaValueIssues.disable({ emitEvent: false });
        }
      });

    this.permissionService
      .hasEveryPermission([Permission.WRITE_DATAPOINT_META_STATS])
      .pipe(take(1))
      .subscribe((hasPermission) => {
        if (!hasPermission) {
          this.form.controls.statsEnabled.disable({ emitEvent: false });
          this.form.controls.statsNumericTransform.disable({ emitEvent: false });
          this.form.controls.statsCalculateNumeric.disable({ emitEvent: false });
        }
      });

    this.permissionService
      .hasEveryPermission([Permission.WRITE_DATAPOINT_GT])
      .pipe(take(1))
      .subscribe((hasPermission) => {
        if (!hasPermission) {
          this.form.controls.groundTruthDatapointField.disable({ emitEvent: false });
        }
      });

    this.permissionService
      .hasEveryPermission([Permission.WRITE_CUSTOMIZE_BUILT_IN])
      .pipe(take(1))
      .subscribe((hasPermission) => {
        if (!hasPermission) {
          this.form.controls.builtIn.disable({ emitEvent: false });
        }
      });
  }

  public submit() {
    if (this.form.valid) {
      const formValue = this.form.value;

      const result: Partial<UserTemplateEntityBase> & { builtIn?: boolean; shortName?: string } = {
        id: this.item()?.id,
        displayName: formValue.displayName,
        description: formValue.description,
        displayShortName: formValue.displayShortName,
        assetShortNames: formValue.assetShortNames,
        referenceNumber: formValue.referenceNumber,
        integrationIdentifier: formValue.integrationIdentifier,
        shared: formValue.accessControl?.shared,
        sharedForEdit: this.form.getRawValue().accessControl?.sharedForEdit,
        template: formValue.templateControl?.template,
        hidden: formValue.templateControl?.hidden,
        templateTenantAccess: this.form.getRawValue().templateControl?.templateTenantAccess,
        canPublish: this.item()?.canPublish,
        canShare: this.item()?.canShare,
      };

      if (this.type() === TemplateModalType.Normal) {
        result.builtIn = !!this.form.getRawValue().builtIn;
        result.shortName = formValue.topicShortName || null;
      }

      return this.type() === TemplateModalType.RuleBase
        ? ({
            ...result,
            verificationType: formValue.verificationType,
            presentationType: this.showPresentationType() ? formValue.presentationType : this.presentationType(),
            groundTruthDatapointField: formValue.groundTruthDatapointField,
            aiContribution: formValue.aiContribution,
            details: formValue.details,
            stats: {
              enabled: formValue.statsEnabled || false,
              numericTransform: formValue.statsNumericTransform || undefined,
              calculateNumeric: formValue.statsCalculateNumeric || false,
            },
            meta: {
              valueDescription: formValue.metaValueDescription || undefined,
              example: formValue.metaExample || undefined,
              valueIssues: formValue.metaValueIssues || undefined,
            },
            ttlSeconds: formValue.ttlSeconds || undefined,
          } as RuleBase)
        : result;
    }
  }
}
